/**
 * Contact inbox — submissions from the public /contact form.
 *
 * Nothing from that form is emailed onward, so this page is the only place
 * a message ever surfaces. It opens on the unread queue for exactly that
 * reason: the default view should be "what still needs answering", not
 * "everything ever sent".
 *
 * Per-title routing to a support@<title> mailbox is the eventual plan;
 * until those mailboxes exist, the publication filter is how one title's
 * messages get looked at.
 *
 * Replies are written here and emailed From service@airage.com. The
 * person's answer goes to that mailbox, not back into this page.
 */
import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import { PubBadge } from '../components/PubBadge'
import { ReplyPanel, ReplySettingsPending } from '../components/ReplyPanel'
import { fmtRelative } from '../lib/format'
import { InboundBlock } from '../components/InboundBlock'
import { chipFor, legacyTimeline, type InboundMessage, type ReplyState } from '../lib/replies'
import { useRepliesEnabled } from '../lib/useRepliesEnabled'

type ContactReply = {
  id: number
  body: string
  to_email: string
  subject: string
  sent_by: string
  created_at: string | null
}

type ContactMessage = {
  id: number
  publication: string
  name: string
  email: string
  account_number: string | null
  subject: string
  message: string
  status: string
  handled_at: string | null
  handled_by: string | null
  created_at: string | null
  replies: ContactReply[]
  inbound: InboundMessage[]
  reply_state: ReplyState | null
}

type ContactMessageList = {
  items: ContactMessage[]
  total: number
  unread: number
}

const SUBJECT_LABELS: Record<string, string> = {
  billing: 'Billing or payment',
  account_access: 'Signing in / access',
  technical: "Something isn't working",
  other: 'Something else',
  phone_call: 'Phone call',
}

const PUBLICATIONS = [
  { value: '', label: 'All titles' },
  { value: 'flight_journal', label: 'Flight Journal' },
  { value: 'model_airplane_news', label: 'Model Airplane News' },
  { value: 'rc_car_action', label: 'RC Car Action' },
  { value: 'diecast', label: 'Die Cast X' },
]

const STATUSES = [
  { value: 'new', label: 'Unread' },
  { value: 'read', label: 'In progress' },
  { value: 'archived', label: 'Archived' },
  { value: '', label: 'All' },
]

export function ContactMessagesPage() {
  const queryClient = useQueryClient()
  // Opens on the queue, not the archive.
  // ?message=<id> (from an email link) opens that message with no status filter.
  const [search] = useSearchParams()
  const linked = Number(search.get('message')) || null
  const [status, setStatus] = useState(linked ? '' : 'new')
  const [publication, setPublication] = useState('')
  const [expanded, setExpanded] = useState<number | null>(linked)
  // Drafts are kept per message so collapsing one doesn't lose the text.
  const [drafts, setDrafts] = useState<Record<number, string>>({})
  const [composing, setComposing] = useState<number | null>(null)

  // The drafted-reply feature 404s when its flag is off; then today's UI stays.
  const replyV2 = useRepliesEnabled()
  const legacy = replyV2.data === false

  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (publication) params.set('publication', publication)
  const qs = params.toString()

  const list = useQuery({
    queryKey: ['contact-messages', status, publication],
    queryFn: () =>
      api.get<ContactMessageList>(`/api/contact-messages${qs ? `?${qs}` : ''}`),
  })

  const setStatusFor = useMutation({
    mutationFn: ({ id, next }: { id: number; next: string }) =>
      api.patch<ContactMessage>(`/api/contact-messages/${id}`, { status: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-messages'] })
      queryClient.invalidateQueries({ queryKey: ['navrail-contact-unread'] })
    },
  })

  const sendReply = useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      api.post<ContactMessage>(`/api/contact-messages/${id}/reply`, { body }),
    onSuccess: (_data, { id }) => {
      setDrafts((d) => {
        const next = { ...d }
        delete next[id]
        return next
      })
      setComposing(null)
      queryClient.invalidateQueries({ queryKey: ['contact-messages'] })
      queryClient.invalidateQueries({ queryKey: ['navrail-contact-unread'] })
    },
  })

  const items = list.data?.items ?? []

  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Members · inbound</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Contact
            <span className="text-amber-glow"> ∙ </span>
            <span className="text-paper-trace">{list.data?.unread ?? 0} unread</span>
          </h1>
          {/* paper-trace is 4.29:1 on ink-900 — fine for a timestamp, thin
              for a paragraph someone is meant to read. */}
          <p className="mt-2 text-sm text-paper-dim max-w-[62ch]">
            {replyV2.data === true
              ? "Each message gets a drafted reply. Review it, edit it and send it below. Replies come from service@airage.com in the title's design; when the customer answers, it shows up here and the message goes back to Unread."
              : 'Messages from the contact form on the member sites. These are not emailed anywhere — this page is the only place they appear. Replies sent from here come from service@airage.com; answers show up under the message.'}
          </p>
        </div>
        <Link to="/help/reply-desk" className="label hover:underline self-end whitespace-nowrap">Reply desk guide</Link>
      </header>

      {/*
        Styled the way every other filter in this app is (see ARReportBucket
        / ARReportAttempts): the admin UI has no `.field` class — that is a
        reader-app idiom, and using it here left these two as unstyled
        browser-default controls on a dark ground.
      */}
      <div className="flex flex-wrap gap-4">
        <div>
          <div className="label mb-1">Status</div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[160px]"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label mb-1">Title</div>
          <select
            value={publication}
            onChange={(e) => setPublication(e.target.value)}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[220px]"
          >
            {PUBLICATIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      {list.isLoading && <p className="text-paper-trace">Loading…</p>}
      {list.isError && (
        <p className="text-red-400">Couldn't load messages. Try again.</p>
      )}

      {!list.isLoading && items.length === 0 && (
        <p className="text-paper-trace">
          Nothing here{status === 'new' ? ' — the queue is empty' : ''}.
        </p>
      )}

      <ul className="space-y-3">
        {items.map((m) => {
          const isOpen = expanded === m.id
          return (
            <li key={m.id} className="border border-ink-500 rounded-sm">
              <button
                type="button"
                className="w-full text-left p-4 flex flex-wrap items-baseline gap-x-3 gap-y-1"
                onClick={() => setExpanded(isOpen ? null : m.id)}
                aria-expanded={isOpen}
              >
                {m.status === 'new' && (
                  <span className="label-amber">NEW</span>
                )}
                <span className="text-paper font-semibold">{m.name}</span>
                <span className="text-paper-trace text-sm">{m.email}</span>
                <span className="label">
                  {SUBJECT_LABELS[m.subject] ?? m.subject}
                </span>
                {/* The console shows publications as a colour-coded badge
                    everywhere else; a raw slug is an internal identifier. */}
                <PubBadge slug={m.publication} compact />
                {(() => { const c = chipFor(m.reply_state); return c && (
                  <span className={`font-mono text-[10px] uppercase tracking-label px-1.5 py-0.5 border rounded-sm ${c.cls}`}>{c.label}</span>
                ) })()}
                <span className="ml-auto text-paper-trace text-xs">
                  {m.created_at ? fmtRelative(m.created_at) : ''}
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-ink-500 pt-3">
                  {m.account_number && (
                    <p className="text-sm text-paper-trace">
                      Account number:{' '}
                      <span className="font-mono text-paper">{m.account_number}</span>
                    </p>
                  )}
                  <p className="text-paper whitespace-pre-wrap">{m.message}</p>
                  {legacy && (m.replies.length > 0 || m.inbound.length > 0) && (
                    <ul className="space-y-2">
                      {legacyTimeline(m.replies, m.inbound).map((e) => e.who === 'us' ? (
                        <li key={e.key} className="border-l-2 border-amber-glow pl-3">
                          <p className="text-xs text-paper-trace">
                            Replied by {e.sentBy}
                            {e.at ? ` · ${fmtRelative(e.at)}` : ''}
                          </p>
                          <p className="text-paper-dim whitespace-pre-wrap text-sm">{e.text}</p>
                        </li>
                      ) : (
                        <li key={e.key}><InboundBlock item={e.inbound!} /></li>
                      ))}
                    </ul>
                  )}
                  {m.handled_by && (
                    <p className="text-xs text-paper-trace">
                      {m.status} by {m.handled_by}
                      {m.handled_at ? ` · ${fmtRelative(m.handled_at)}` : ''}
                    </p>
                  )}
                  {replyV2.data === true && <ReplyPanel channel="contact" messageId={m.id} />}
                  {!legacy && replyV2.data !== true && <ReplySettingsPending query={replyV2} />}
                  {legacy && composing === m.id && (
                    <div className="space-y-2">
                      <label className="block">
                        <span className="label">
                          Reply to {m.email} · from service@airage.com
                        </span>
                        <textarea
                          rows={8}
                          autoFocus
                          value={drafts[m.id] ?? ''}
                          onChange={(e) =>
                            setDrafts((d) => ({ ...d, [m.id]: e.target.value }))
                          }
                          className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 font-sans"
                        />
                      </label>
                      <p className="text-xs text-paper-trace">
                        Signed "Air Age Customer Service", with their message quoted below.
                      </p>
                      {sendReply.isError && sendReply.variables?.id === m.id && (
                        <p className="text-red-400 text-sm">
                          {replyErrorText(sendReply.error)}
                        </p>
                      )}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          className="label-amber hover:underline disabled:opacity-50"
                          disabled={sendReply.isPending || !(drafts[m.id] ?? '').trim()}
                          onClick={() =>
                            sendReply.mutate({ id: m.id, body: drafts[m.id] ?? '' })
                          }
                        >
                          {sendReply.isPending ? 'Sending…' : 'Send reply'}
                        </button>
                        <button
                          type="button"
                          className="label hover:underline"
                          disabled={sendReply.isPending}
                          onClick={() => {
                            setComposing(null)
                            sendReply.reset()
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {legacy && composing !== m.id && (
                      <button
                        type="button"
                        className="label-amber hover:underline"
                        onClick={() => {
                          sendReply.reset()
                          setComposing(m.id)
                        }}
                      >
                        Reply
                      </button>
                    )}
                    {STATUSES.filter((s) => s.value && s.value !== m.status).map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        className="label hover:underline"
                        disabled={setStatusFor.isPending}
                        onClick={() =>
                          setStatusFor.mutate({ id: m.id, next: s.value })
                        }
                      >
                        Mark {s.label.toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function replyErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.detail as { detail?: unknown } | null
    if (d && typeof d.detail === 'string') return d.detail
    return `Not sent (error ${err.status}).`
  }
  return 'Not sent. Check your connection and try again.'
}
